import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ArrowDownCircle } from 'lucide-react';
import { SellCreditsCashModal, type SellCreditsCashFormValues } from '@kioskgaming/ui';
import AvailableCreditsCallout from '../AvailableCreditsCallout';
import { dispatchShopCreditBalanceRefresh } from '../../constants';
import { fetchDashboard, postShopPlayerCredits } from '../../services/api';
import {
  parseLowBalanceWarningSettings,
  type LowBalanceWarningSettings,
} from '../../utils/lowBalanceWarning';

export type SellCreditsCashToPlayerTriggerProps = {
  playerId: string;
  playerName: string;
  /** Pre-loaded shop credit balance (optional — fetched on open if omitted). */
  creditBalance?: number | null;
  creditBalanceLoading?: boolean;
  lowBalanceWarning?: LowBalanceWarningSettings;
  shopCostRate?: number | null;
  onSuccess?: () => void;
  disabled?: boolean;
  buttonText?: string;
  className?: string;
  defaultOpen?: boolean;
};

export default function SellCreditsCashToPlayerTrigger({
  playerId,
  playerName,
  creditBalance: creditBalanceProp,
  creditBalanceLoading: creditBalanceLoadingProp,
  lowBalanceWarning: lowBalanceWarningProp,
  shopCostRate: shopCostRateProp,
  onSuccess,
  disabled = false,
  buttonText = 'Deposit',
  className = '',
  defaultOpen = false,
}: SellCreditsCashToPlayerTriggerProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(defaultOpen);
  const [balance, setBalance] = useState<number | null>(creditBalanceProp ?? null);
  const [balanceLoading, setBalanceLoading] = useState(creditBalanceLoadingProp ?? false);
  const [lowBalanceWarning, setLowBalanceWarning] = useState<LowBalanceWarningSettings>(
    lowBalanceWarningProp ?? { enabled: true, effectiveThresholdUsd: null },
  );
  const [shopCostRate, setShopCostRate] = useState<number | null>(shopCostRateProp ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data) {
        const raw = res.data.credit_balance;
        const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
        setBalance(Number.isFinite(n) ? n : null);
        setLowBalanceWarning(parseLowBalanceWarningSettings(res.data));
        const rateRaw = res.data.cost_rate;
        const rate = rateRaw != null ? Number(rateRaw) : NaN;
        setShopCostRate(Number.isFinite(rate) ? Math.min(1, Math.max(0, rate)) : null);
      } else {
        setBalance(null);
      }
    } catch {
      setBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (creditBalanceProp !== undefined) setBalance(creditBalanceProp);
  }, [creditBalanceProp]);

  useEffect(() => {
    if (creditBalanceLoadingProp !== undefined) setBalanceLoading(creditBalanceLoadingProp);
  }, [creditBalanceLoadingProp]);

  useEffect(() => {
    if (lowBalanceWarningProp !== undefined) setLowBalanceWarning(lowBalanceWarningProp);
  }, [lowBalanceWarningProp]);

  useEffect(() => {
    if (shopCostRateProp !== undefined && shopCostRateProp !== null) {
      setShopCostRate(shopCostRateProp);
    }
  }, [shopCostRateProp]);

  useEffect(() => {
    if (!open) return;
    if (
      creditBalanceProp === undefined ||
      shopCostRateProp === undefined ||
      lowBalanceWarningProp === undefined
    ) {
      void loadDashboard();
    }
    setError(null);
  }, [open, loadDashboard, creditBalanceProp, shopCostRateProp, lowBalanceWarningProp]);

  const handleSubmit = async (values: SellCreditsCashFormValues) => {
    if (balance != null && values.creditAmount > balance) {
      const msg = 'Not enough Shop credits for this deposit. Buy credits or lower the amount.';
      setError(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await postShopPlayerCredits(playerId, {
        action: 'add',
        credits: values.creditAmount,
        amount: values.creditAmount,
        cash_received: values.cashReceived,
        payment_method: values.paymentMethod,
        received_at: values.receivedAt,
        external_reference: values.externalReference,
        note: values.note,
      });
      if (!res.success) {
        const msg = res.message || 'Deposit failed';
        setError(msg);
        toast.error(msg);
        return;
      }
      toast.success('Credits deposited to Player');
      setOpen(false);
      dispatchShopCreditBalanceRefresh();
      onSuccess?.();

      const cashTx = res.data?.cash_transaction as { id?: string; createdAt?: string } | undefined;
      if (cashTx?.id && cashTx?.createdAt) {
        navigate(
          `/cash-settlement/deposits/${cashTx.id}?created_at=${encodeURIComponent(String(cashTx.createdAt))}`,
        );
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Deposit failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const btnClass =
    'inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={`${btnClass} ${className}`}
      >
        <ArrowDownCircle className="h-4 w-4 shrink-0" aria-hidden />
        {buttonText}
      </button>

      <SellCreditsCashModal
        open={open}
        onClose={() => setOpen(false)}
        flow="shop_player_deposit"
        buyerName={playerName}
        buyerCostRate={1}
        sellerCostRate={shopCostRate ?? undefined}
        submitting={submitting}
        error={error}
        onSubmit={handleSubmit}
      />

      {open ? (
        <div className="sr-only" aria-live="polite">
          <AvailableCreditsCallout
            balance={balance}
            loading={balanceLoading}
            lowBalanceWarning={lowBalanceWarning}
          />
        </div>
      ) : null}
    </>
  );
}
